import { Facebook } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { CommentAutomationTab } from '@/components/comments/CommentAutomationTab'
import { CommentsTab } from '@/components/comments/CommentsTab'
import { CatalogTab } from '@/components/facebook/CatalogTab'
import { MarketingMessagesTab } from '@/components/facebook/MarketingMessagesTab'
import { UtilityTemplatesTab } from '@/components/facebook/UtilityTemplatesTab'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTranslations } from '@/hooks/use-translations'
import { useMessengerStatusQuery } from '@/queries/meta'

/**
 * Comments on the Facebook Page connected through Messenger and their
 * automatic reply, plus the Messenger messages allowed outside the 24-hour
 * window: utility templates and paid marketing messages, and the business's
 * product catalog.
 */
export function FacebookPage() {
  const { t } = useTranslations()
  const { lang } = useParams<{ lang: string }>()
  const status = useMessengerStatusQuery()
  const channelsHref = `/${lang}/settings?tab=channels`
  const pageName = status.data?.metadata?.page_name

  return (
    <div className="container mx-auto space-y-8 py-6">
      <PageHeader
        title={t('facebook_page.title')}
        description={status.data?.connected && pageName
          ? t('facebook_page.connected_as', { page: pageName })
          : t('facebook_page.description')}
      >
        {status.data?.connected && (
          <Button asChild variant="outline" size="sm">
            <Link to={channelsHref}>{t('facebook_page.manage_connection')}</Link>
          </Button>
        )}
      </PageHeader>

      {status.isLoading && <Skeleton className="h-64 w-full" />}

      {!status.isLoading && !status.data?.connected && (
        <EmptyState
          icon={<Facebook className="h-6 w-6" />}
          title={t('facebook_page.not_connected_title')}
          description={t('facebook_page.not_connected_description')}
        >
          <Button asChild>
            <Link to={channelsHref}>{t('facebook_page.connect')}</Link>
          </Button>
        </EmptyState>
      )}

      {status.data?.connected && (
        <Tabs defaultValue="comments" className="space-y-8">
          <TabsList>
            <TabsTrigger value="comments">{t('facebook_page.tabs.comments')}</TabsTrigger>
            <TabsTrigger value="automation">{t('facebook_page.tabs.automation')}</TabsTrigger>
            <TabsTrigger value="utility">{t('facebook_page.tabs.utility')}</TabsTrigger>
            <TabsTrigger value="marketing">{t('facebook_page.tabs.marketing')}</TabsTrigger>
            <TabsTrigger value="catalog">{t('facebook_page.tabs.catalog')}</TabsTrigger>
          </TabsList>
          <TabsContent value="comments"><CommentsTab channel="facebook" /></TabsContent>
          <TabsContent value="automation"><CommentAutomationTab channel="facebook" /></TabsContent>
          <TabsContent value="utility"><UtilityTemplatesTab /></TabsContent>
          <TabsContent value="marketing"><MarketingMessagesTab /></TabsContent>
          <TabsContent value="catalog"><CatalogTab /></TabsContent>
        </Tabs>
      )}
    </div>
  )
}
